import { CircleText, QuestionDisplayMode } from '@doltech/ui/lib/figma/CircleText/CircleText';
import { CollapseExpandPanel } from '@doltech/ui/lib/figma/CollapseExpandPanel/CollapseExpandPanel';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/Box';
import { getTooltipTitle } from '@doltech/ui/lib/figma/MultipleChoiceQuestionResult/MultipleChoiceQuestionResult';
import { QuestionBox, QuestionDropable } from '@doltech/ui/lib/figma/Question/QuestionDropable';
import { MarkedTypeEnum, TestModeEnum } from '@doltech/utils/lib/constants';
import { Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { useTestResultStore } from '../../model/test-result.hook';

interface NodeFormat {
  text: string;
  circleText: string;
}

export interface MatchingQuestionResultProps extends React.HTMLAttributes<HTMLDivElement> {
  no: number;
  markedStatus: MarkedTypeEnum;

  correct: NodeFormat;
  wrong: NodeFormat;
  headerTitle: string;
  questionsInGroup?: any[];
}

const Main = styled(Box)``;
const StyledQuestionBox = styled(QuestionBox)`
  .question-box-wrapper {
    padding: 4px;
  }
  &:hover {
    border: none;
    box-shadow: none;
  }

  &.${QuestionDisplayMode.QUESTION_OPTION_MISSING} {
    .question-box-wrapper {
      background-color: ${colorsV2.black5};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_WRONG} {
    .question-box-wrapper {
      background-color: ${colorsV2.primary10};
    }
  }
  &.${QuestionDisplayMode.QUESTION_OPTION_CORRECT} {
    .question-box-wrapper {
      background-color: ${colorsV2.green10};
    }
  }
`;
export const MatchingQuestionResult = observer((props: MatchingQuestionResultProps) => {
  const { no, markedStatus, correct, wrong, headerTitle, questionsInGroup } = props;

  const store = useTestResultStore();

  const shouldShowTooltip = store.testMode !== TestModeEnum.EXPLANATION;

  const correctNode = (correctObj, withTooltip = false) => ({
    color: colorsV2.green100,
    renderElement: (
      <StyledQuestionBox
        displayMode={
          (withTooltip && shouldShowTooltip
            ? QuestionDisplayMode.QUESTION_OPTION_CORRECT
            : '') as any
        }
      >
        <Tooltip
          title={
            shouldShowTooltip && withTooltip
              ? getTooltipTitle({ markedStatus: MarkedTypeEnum.CORRECT })
              : ''
          }
        >
          <CircleText
            displayMode={QuestionDisplayMode.QUESTION_OPTION_CORRECT}
            circleText={correctObj.circleText}
            text={correctObj.text}
            selected
          />
        </Tooltip>
      </StyledQuestionBox>
    ),
  });

  const wrongNode = (wrongObj) => ({
    color: colorsV2.red100,
    renderElement: (
      <StyledQuestionBox displayMode={QuestionDisplayMode.QUESTION_OPTION_WRONG}>
        <Tooltip
          title={shouldShowTooltip ? getTooltipTitle({ markedStatus: MarkedTypeEnum.WRONG }) : ''}
        >
          <CircleText
            displayMode={QuestionDisplayMode.QUESTION_OPTION_WRONG}
            selected
            circleText={wrongObj.circleText}
            text={wrongObj.text}
          />
        </Tooltip>
      </StyledQuestionBox>
    ),
  });

  const missingNode = (correctObj) => ({
    color: colorsV2.green100,
    renderElement: (
      <StyledQuestionBox displayMode={QuestionDisplayMode.QUESTION_OPTION_MISSING}>
        <Tooltip
          title={shouldShowTooltip ? getTooltipTitle({ markedStatus: MarkedTypeEnum.MISSING }) : ''}
        >
          <CircleText
            displayMode={QuestionDisplayMode.QUESTION_OPTION_CORRECT}
            circleText={correctObj.circleText}
            text={correctObj.text}
            selected
          />
        </Tooltip>
      </StyledQuestionBox>
    ),
  });

  const questionNodes = (markedStatus2, correctObj, wrongObj) => {
    switch (markedStatus2) {
      case MarkedTypeEnum.MISSING: {
        return [missingNode(correctObj)];
      }
      case MarkedTypeEnum.WRONG: {
        return [wrongNode(wrongObj), correctNode(correctObj)];
      }
      default:
        return [correctNode(correctObj, true)];
    }
  };

  const [expand, setExpand] = React.useState(false);

  const matchingQuestionCollapsed = (
    <QuestionDropable
      key={no}
      no={no}
      title={headerTitle}
      markedStatus={markedStatus}
      nodes={questionNodes(markedStatus, correct, wrong)}
    />
  );

  const matchingQuestionExpanded = questionsInGroup.map((question) => (
    <QuestionDropable
      key={question.no}
      no={question.no}
      title={question.headerTitle}
      markedStatus={markedStatus}
      nodes={questionNodes(question.markedStatus, question.correct, question.wrong)}
    />
  ));

  return (
    <Main
      as={CollapseExpandPanel}
      onToggleCollapse={() => setExpand((oldExpandValue) => !oldExpandValue)}
      expand={expand}
      collapsedSection={matchingQuestionCollapsed}
      expandedSection={matchingQuestionExpanded}
      {...props}
    />
  );
});
